import { ArrowDown, Mark } from '@sds-system/assets';
import { Row, Icon as SdsIcon } from '@sds-system/components';
import type { PolymorphicRef } from '@sds-system/types';
import { deepMerge, useGetInternalProps } from '@sds-system/utils';
import type { ElementType } from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { Box } from '../Box';
import { Typography } from '../Typography';
import { StyledInfoboxContent, StyledInfoboxHeader, StyledInfoboxRoot } from './Infobox.styled';
import type { InfoboxComponent, InfoboxProps } from './Infobox.types';

export const Infobox: InfoboxComponent = forwardRef(
  <C extends ElementType = 'div'>(props: InfoboxProps<C>, ref: PolymorphicRef<C>) => {
    const [innerOpen, setInnerOpen] = useState(() => {
      if (props.defaultIsOpen !== undefined) return props.defaultIsOpen;
      if (props.isOpen !== undefined) return props.isOpen;
      if (props.onOpenChange !== undefined) return false;
      return undefined;
    });
    const [height, setHeight] = useState<{ close: number; open: number } | undefined>();
    const titleRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);

    const internalProps = useGetInternalProps('Infobox');

    const {
      Icon,
      title,
      content,
      variant = 'basic',
      styledComponents,
      ...mergedProps
    } = deepMerge(props, internalProps);

    useEffect(() => {
      const titleHeight = titleRef.current?.clientHeight || 0;
      const contentHeight = contentRef.current?.clientHeight || 0;

      setHeight({
        close: titleHeight,
        open: titleHeight + contentHeight,
      });
    }, []);

    useEffect(() => {
      if (props?.isOpen === undefined) return;
      setInnerOpen(props?.isOpen);
    }, [props?.isOpen]);

    const handleClickOpenIndicator = () => {
      if (innerOpen === undefined) return;
      setInnerOpen((p) => !p);
      props?.onOpenChange?.(innerOpen);
    };

    return (
      <StyledInfoboxRoot
        className='infobox-root'
        ref={ref}
        variant={variant}
        {...mergedProps}
        {...styledComponents?.root}
      >
        <Box
          alignItems='flex-start'
          css={{
            transition: 'height 0.3s',
            overflow: 'hidden',
          }}
          display='flex'
          flexDirection={title ? 'column' : 'row'}
          gap={4}
          height={
            innerOpen !== undefined ? (innerOpen ? height?.open : height?.close) : 'max-content'
          }
          width={'100%'}
        >
          {title && (
            <StyledInfoboxHeader
              className='infobox-header-wrapper'
              ref={titleRef}
              {...styledComponents?.header}
            >
              <Row alignItems='center' gap={4}>
                {Icon || (Icon === undefined ? <SdsIcon size='2XS' source={Mark} /> : null)}
                <Typography fontType='body2.medium'>{title}</Typography>
              </Row>

              {innerOpen !== undefined && (
                <SdsIcon
                  color='icon.tertiary'
                  css={{
                    transform: `rotate(${innerOpen ? '180deg' : '0deg'})`,
                    transition: 'transform 0.3s',
                    cursor: 'pointer',
                  }}
                  onClick={handleClickOpenIndicator}
                  size='XS'
                  source={ArrowDown}
                />
              )}
            </StyledInfoboxHeader>
          )}
          {content && (
            <StyledInfoboxContent
              className='infobox-content'
              hasTitle={!!title}
              ref={contentRef}
              {...styledComponents?.content}
            >
              {content}
            </StyledInfoboxContent>
          )}
        </Box>
      </StyledInfoboxRoot>
    );
  },
);
